import React from "react";
import { HiSparkles } from "react-icons/hi2";
import { IoCheckmarkCircle, IoWarning, IoClose } from "react-icons/io5";
import style from "./aiAssist.module.css";

/**
 * The "AI is analyzing your video…" panel shown at the top of the Add Content
 * form while a job runs, and its terminal (complete/failed) states.
 *
 * Stateless: all state comes from `useAiVideoAnalysis` via props. It renders
 * three things — a headline, a step-by-step stage tracker
 * (Uploading → Extracting Audio → … → Complete), and a progress bar.
 *
 * @param {string}  status       idle|running|complete|failed
 * @param {Array}   stages       [{ id, label, weight }]
 * @param {string}  activeStage  current stage id (or "complete")
 * @param {number}  progress     0..100
 * @param {string}  [error]
 * @param {number}  [filledCount] number of fields auto-filled (complete state)
 * @param {function} onRetry
 * @param {function} onDismiss
 */
const AiAnalysisProgress = ({
  status,
  stages = [],
  activeStage,
  progress = 0,
  error,
  filledCount = 0,
  onRetry,
  onDismiss,
}) => {
  if (status === "idle") return null;

  const isRunning = status === "running";
  const isComplete = status === "complete";
  const isFailed = status === "failed";

  const activeIndex = stages.findIndex((s) => s.id === activeStage);
  const stageState = (idx) => {
    if (isComplete) return "done";
    if (isFailed) return idx < activeIndex ? "done" : idx === activeIndex ? "error" : "todo";
    if (activeStage === "complete") return "done";
    if (idx < activeIndex) return "done";
    if (idx === activeIndex) return "active";
    return "todo";
  };

  return (
    <div
      className={`${style.aiPanel} ${isFailed ? style.aiPanelError : ""} ${
        isComplete ? style.aiPanelDone : ""
      }`}
      role="status"
      aria-live="polite"
    >
      <div className={style.aiPanelHead}>
        <div className={style.aiPanelHeadLeft}>
          <span className={`${style.aiPanelIcon} ${isRunning ? style.aiPulse : ""}`}>
            {isComplete ? (
              <IoCheckmarkCircle />
            ) : isFailed ? (
              <IoWarning />
            ) : (
              <HiSparkles />
            )}
          </span>
          <div>
            <p className={style.aiPanelTitle}>
              {isRunning && "AI is analyzing your video…"}
              {isComplete && "AI analysis complete"}
              {isFailed && "AI analysis couldn't finish"}
            </p>
            <p className={style.aiPanelSub}>
              {isRunning &&
                "Sit tight — we're generating metadata suggestions. You can keep editing while this runs."}
              {isComplete &&
                (filledCount > 0
                  ? `${filledCount} field${filledCount === 1 ? "" : "s"} auto-filled. Review everything below — every value stays editable.`
                  : "Review the suggestions below. Every value stays editable.")}
              {isFailed &&
                (error ||
                  "Something went wrong. You can retry, or just fill in the details manually and publish as usual.")}
            </p>
          </div>
        </div>

        <div className={style.aiPanelHeadRight}>
          {isFailed && onRetry && (
            <button type="button" className={style.aiRetryBtn} onClick={onRetry}>
              Retry
            </button>
          )}
          {(isComplete || isFailed) && onDismiss && (
            <button
              type="button"
              className={style.aiDismissBtn}
              onClick={onDismiss}
              aria-label="Dismiss AI panel"
            >
              <IoClose />
            </button>
          )}
        </div>
      </div>

      {/* Step-by-step stage tracker */}
      {!isFailed && (
        <ol className={style.aiSteps}>
          {stages.map((stage, idx) => {
            const s = stageState(idx);
            return (
              <li
                key={stage.id}
                className={`${style.aiStep} ${style[`aiStep_${s}`] || ""}`}
              >
                <span className={style.aiStepDot}>
                  {s === "done" ? <IoCheckmarkCircle /> : idx + 1}
                </span>
                <span className={style.aiStepLabel}>{stage.label}</span>
              </li>
            );
          })}
          <li
            className={`${style.aiStep} ${
              isComplete ? style.aiStep_done : style.aiStep_todo
            }`}
          >
            <span className={style.aiStepDot}>
              {isComplete ? <IoCheckmarkCircle /> : stages.length + 1}
            </span>
            <span className={style.aiStepLabel}>Complete</span>
          </li>
        </ol>
      )}

      {/* Progress bar */}
      {!isFailed && (
        <div className={style.aiProgressTrack}>
          <div
            className={style.aiProgressFill}
            style={{ width: `${isComplete ? 100 : progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default AiAnalysisProgress;
